import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, TextField, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function CancelModal({ open, onClose, sub, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/cancel", {
        method: "POST",
        body: { subscriptionId: sub.id, cancellationReason: reason || "Admin cancelled", cancellationFeedback: "Cancelled via admin dev tools" },
      });
      if (!res.ok) throw new Error(res.error || "Failed to cancel subscription");
      onSuccess("Subscription cancelled successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to cancel subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.red }}>Cancel Subscription</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will cancel the subscription. The customer will need to re-subscribe.
        </Alert>
        <TextField fullWidth label="Cancellation Reason" value={reason} onChange={(e) => setReason(e.target.value)}
          multiline rows={2} placeholder="e.g., Customer requested cancellation" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Back</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}
          sx={{ bgcolor: COLORS.red, "&:hover": { bgcolor: "#B43A30" }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
